<script setup>
import {computed, onMounted, ref} from "vue";
import FormStatisticReport from './FormStatisticReport.vue';
import FieldStatistic from "./FieldStatistic.vue";
import FieldResponses from "./FieldResponses.vue";

const props = defineProps({
    formId: {
        type: Number,
        required: true,
    },
    userId: {
        type: Number,
        required: false,
        default: null
    },
    chartColor: {
        type: String,
        required: false,
        default: '#666666',
    }
})

const statisticsLoaded = ref(false)
const form = ref({
    id: '',
    slug: '',
    title: '',
    response_count: '',
})
const statistics = ref({})
const submissions = ref({})
const user = ref(null)
const presenterMode = ref(false)
const displaySubmissions = ref(false)
const submissionSearchTerm = ref('')

const loadStatistics = async () => {
    let params = {}

    if (props.userId !== null) {
        params = {params: {user_id: props.userId}}
    }

    user.value = null

    const res = await axios.get(route('api.form.statistic.show', props.formId), params)
    statistics.value = res.data.statistics
    submissions.value = res.data.submissions
    user.value = res.data.user
    form.value.id = res.data.form.id
    form.value.slug = res.data.form.slug
    form.value.title = res.data.form.title
    form.value.response_count = res.data.form.response_count
    statisticsLoaded.value = true
}

const statsAvailable = computed(() => {
    // Check if any field in statistics.fields has stats_available === true
    if (! statistics.value.fields) {
        return false
    }
    return Object.values(statistics.value.fields).some(field => field.stats_available)
})

const filteredSubmissions = computed(() => {
    return submissions.value.filter(submission => submission.user.name.toLowerCase().includes(submissionSearchTerm.value.toLowerCase()))
})

onMounted(() => {
    loadStatistics()
})

</script>
<template>
    <div
        :class="presenterMode ? 'fixed inset-0 z-modal bg-white overflow-y-scroll px-4 md:px-6 lg:px-8' : ''"
    >
        <div
            v-if="! presenterMode"
            class="md:sticky top-2 z-topbar transition-transform"
        >
            <frosted-bar>
                <div class="flex items-center justify-between space-x-4">
                    <h1>
                        <a
                            :href="route('forms.edit', form.id)"
                            class="mf-link"
                        >
                            {{ form.title }}
                        </a>
                    </h1>
                    <div class="flex items-center justify-end space-x-2">
                        <a
                            v-if="form"
                            :href="route('form-submissions.create', form.slug)"
                            target="_blank"
                        >
                            <button-secondary>{{ _mft('shared:action.show') }}</button-secondary>
                        </a>
                        <button-secondary
                            @click="presenterMode = !presenterMode"
                        >Present</button-secondary>
                    </div>
                </div>
                <template #navigation>
                    <a
                        :href="route('forms.index')"
                        class="mf-link text-xs"
                    >
                        {{ _mft('form:form.domainNamePlural') }}
                    </a>
                </template>
            </frosted-bar>
        </div>

        <span
            v-if="presenterMode"
            class="fixed top-2 right-2"
        >
            <button-secondary
                size="md"
                @click="presenterMode = !presenterMode"
            >
                <i class="far fa-times"></i>
            </button-secondary>
        </span>

        <card-basic class="mt-8">
            <div
                v-if="props.userId === null"
                class="flex items-center justify-between space-x-4"
            >
                <span>
                    {{ _mft('form:form.submissions.userCount', {count: submissions.length}) }}
                </span>
                <button-default
                    @click="displaySubmissions = ! displaySubmissions"
                >
                    {{ displaySubmissions ? _mft('shared:hide') : _mft('shared:filter') }}
                </button-default>
            </div>

            <div
                v-if="props.userId !== null && user !== null"
                class="flex items-center justify-between space-x-4"
            >
                <span>
                     {{ user.name }}
                </span>
                <a :href="route('forms.statistics', [props.formId])">
                    <button-default size="sm">
                        <i class="fa-regular fa-close"></i>
                    </button-default>
                </a>
            </div>

            <div
                v-if="displaySubmissions"
                class="mt-2"
            >
                <div class="flex justify-between items-center mt-4 mb-2">
                    <h2>
                        {{ _mft('form:form.submissions.users') }}
                    </h2>
                    <input-text
                        v-model:content="submissionSearchTerm"
                        :autofocus="true"
                        size="sm"
                        :placeholder="_mft('shared:search')"
                        class="min-w-48 !mb-0"
                    ></input-text>
                </div>
                <ul class="divide-y">
                    <li
                        v-for="submission in filteredSubmissions"
                        :key="submission.id"
                        class="py-1"
                    >
                        <a
                            :href="route('forms.statistics-user', [props.formId, submission.user.id])"
                            class="block hover:text-gray-400"
                        >
                            {{ submission.user.name }}
                        </a>
                    </li>
                </ul>
            </div>

        </card-basic>

        <ui-tabbed>
            <ui-tabbed-tab
                :active="true"
                :title="'Fields'"
                :value="'fields'"
            >
                <div
                    :class="presenterMode ? '' : 'mt-12'"
                >
                    <div
                        v-if="! statisticsLoaded"
                        class="flex justify-center"
                    >
                        <mf-spinner-medium class="w-24 h-12" />
                    </div>

                    <div
                        v-else
                        class="space-y-12"
                    >
                        <div
                            v-if="presenterMode"
                            class="min-h-screen flex items-center justify-center"
                        >
                            <div>
                                <h1 class="text-3xl">{{ form.title}}</h1>
                                <p class="lowercase text-center">{{ form.response_count }} {{ _mft('form:statistics.responses') }}</p>
                            </div>
                        </div>

                        <p
                            v-if="form.response_count === 0"
                            class="text-center text-gray-500"
                        >
                            {{ _mft('form:form.noResponses') }}
                        </p>

                        <div
                            v-for="(field, f) in statistics.fields"
                            class="mb-4"
                            :class="presenterMode ? 'min-h-screen' : ''"
                        >
                            <h2>
                                {{ field.title}}
                            </h2>
                            <div class="text-xs text-gray-500 mb-2">
                                {{ _mft('blocks:'+field.type) }}
                            </div>
                            <div>
                                <div
                                    v-for="(response, r) in field.responses"
                                    class="hidden"
                                >
                                    {{ response }}
                                </div>
                                <div class="grid grid-cols-6 gap-4">
                                    <field-statistic
                                        v-for="(stat, s) in field.stats"
                                        v-if="field.stats_available"
                                        v-show="!s.includes('_raw')"
                                        :type="s"
                                        :stat="stat"
                                        :chart-color="chartColor"
                                    />
                                    <field-responses
                                        v-else
                                        :field="field"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </ui-tabbed-tab>
            <ui-tabbed-tab
                v-if="statsAvailable"
                :title="'Fields as line graph'"
                :value="'field-graph'"
            >
                <FormStatisticReport :statistics="statistics" />
            </ui-tabbed-tab>
        </ui-tabbed>
    </div>
</template>
